import { Secret } from 'kubernetes-types/core/v1';

import { useAuthorizations, useCurrentUser } from '@/react/hooks/useUser';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { UserId } from '@/portainer/users/types';

import { appOwnerIdLabel } from '../applications/constants';

import { configurationOwnerIdLabel } from './constants';
import { SecretRowData } from './ListView/SecretsDatatable/types';

export function useIsSecretRestricted(secret: Secret) {
  const { user } = useCurrentUser();
  const isSecretsRestricted = useAreSecretsRestricted();
  const { authorized } = useAuthorizations('K8sClusterW');

  return getIsSecretRestricted(
    secret,
    user.Id,
    authorized,
    isSecretsRestricted
  );
}

export function useIsSecretRowRestricted(secret: SecretRowData) {
  const { user } = useCurrentUser();
  const isSecretsRestricted = useAreSecretsRestricted();
  const { authorized } = useAuthorizations('K8sClusterW');

  return getIsSecretRowRestricted(
    secret,
    user.Id,
    authorized,
    isSecretsRestricted
  );
}

function useAreSecretsRestricted() {
  const environmentId = useEnvironmentId();
  const restrictSecretsQuery = useEnvironment(
    environmentId,
    (env) => env?.Kubernetes.Configuration.RestrictSecrets
  );
  return !!restrictSecretsQuery.data;
}

export function getIsSecretRowRestricted(
  secret: SecretRowData,
  userId: UserId,
  isEnvironmentAdmin: boolean,
  isSecretsRestricted: boolean
) {
  const userOwnsSecret = secret.ConfigurationOwnerId === `${userId}`;
  return isSecretsRestricted && !isEnvironmentAdmin && !userOwnsSecret;
}

function getIsSecretRestricted(
  secret: Secret,
  userId: UserId,
  isEnvironmentAdmin: boolean,
  isSecretsRestricted: boolean
) {
  const userOwnsSecret =
    secret?.metadata?.labels?.[configurationOwnerIdLabel] === `${userId}` ||
    // portainer adds the label 'io.portainer.kubernetes.application.owner.id' to all resources created via manifest
    // secrets made via manifest will have this label instead of the 'io.portainer.kubernetes.configuration.owner.id' label
    secret?.metadata?.labels?.[appOwnerIdLabel] === `${userId}`;

  return isSecretsRestricted && !isEnvironmentAdmin && !userOwnsSecret;
}
